import React, { useEffect, useMemo, useState } from "react";

const SOURCE_OPTIONS = [
  { value: "workspace-marketing", label: "Marketing Workspace" },
  { value: "workspace-product", label: "Product Catalog" },
  { value: "workspace-editorial", label: "Editorial Pipeline" }
];

const SAMPLE_ITEMS = [
  {
    id: "item-01",
    title: "Summer Recipe Launch",
    description: "Seasonal dishes and hero imagery ready for the blog.",
    link: "https://gymplix.com/csv/recipe-launch",
    category: "Editorial",
    date: "Jan 20, 2026",
    imageLabel: "Recipe"
  },
  {
    id: "item-02",
    title: "Smart Appliance Overview",
    description: "Feature table for the new smart kitchen bundle.",
    link: "https://gymplix.com/csv/appliance-overview",
    category: "Product",
    date: "Jan 22, 2026",
    imageLabel: "Product"
  },
  {
    id: "item-03",
    title: "Weight-Loss Program Steps",
    description: "Editorial copy with targeted keywords for SEO campaign.",
    link: "https://gymplix.com/csv/weight-loss",
    category: "Campaign",
    date: "Jan 26, 2026",
    imageLabel: "Guide"
  },
  {
    id: "item-04",
    title: "Weekly Harvest Spotlight",
    description: "Curated ingredient list that feeds into the csv export.",
    link: "https://gymplix.com/csv/harvest-spotlight",
    category: "Editorial",
    date: "Jan 28, 2026",
    imageLabel: "Spotlight"
  }
];

const generateAutoName = () => {
  const stamp = new Date().toISOString().slice(0, 16).replace("T", "_").replace(/:/g, "");
  return `auto-generated-name_${stamp}`;
};

const CsvGenerator = () => {
  const [selectedSource, setSelectedSource] = useState("");
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [exportName, setExportName] = useState("");
  const [exportOptions, setExportOptions] = useState({
    removeLinks: false,
    includeHeaders: true,
    optimizeExcel: true
  });
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const autoNamePlaceholder = useMemo(() => generateAutoName(), []);

  useEffect(() => {
    if (!selectedSource) {
      setItems([]);
      setSelectedItems([]);
      setIsLoadingItems(false);
      return;
    }

    setSelectedItems([]);
    setIsLoadingItems(true);
    const timeout = setTimeout(() => {
      setItems(SAMPLE_ITEMS);
      setIsLoadingItems(false);
    }, 450);

    return () => clearTimeout(timeout);
  }, [selectedSource]);

  const handleSelectItem = (itemId) => {
    setSelectedItems((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      }
      return [...prev, itemId];
    });
  };

  const handleSelectAll = (checked) => {
    setSelectedItems(checked ? items.map((item) => item.id) : []);
  };

  const toggleExportOption = (key) => {
    setExportOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const previewRows = useMemo(
    () => items.filter((item) => selectedItems.includes(item.id)),
    [items, selectedItems]
  );

  const visiblePreviewRows = previewRows.slice(0, 5);
  const previewBadgeText = `${selectedItems.length} rows selected`;
  const isExportDisabled = selectedItems.length === 0;
  const isSelectAllChecked = items.length > 0 && selectedItems.length === items.length;
  const sourceStatus = selectedSource ? "Connected" : "Not connected";
  const sourceStatusClasses = selectedSource
    ? "bg-emerald-100 text-emerald-700"
    : "bg-rose-100 text-rose-700";

  return (
    <div className="min-h-screen bg-slate-50 py-10">
      <div className="mx-auto max-w-6xl px-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600/80">
              Workspace tools
            </p>
            <h1 className="text-3xl font-semibold text-slate-900">CSV Generator</h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">
              Select items and generate export-ready CSV files for campaigns, product feeds, or editorial
              drops.
            </p>
          </div>
          <button
            type="button"
            className="group inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-900"
          >
            <svg
              className="h-4 w-4 text-slate-500 transition group-hover:text-slate-700"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 4h14M3 10h14M3 16h8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            Export History
          </button>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.1fr_0.85fr]">
          {/* Left column with source + items */}
          <div className="space-y-6">
            {/* Select Source Card */}
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Select Source</h2>
                  <p className="text-sm text-slate-500">Choose the workspace that should power the export.</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${sourceStatusClasses}`}>
                  {sourceStatus}
                </span>
              </div>
              <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Data source
              </label>
              <select
                value={selectedSource}
                onChange={(event) => setSelectedSource(event.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
              >
                <option value="">Choose a data source…</option>
                {SOURCE_OPTIONS.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-slate-500">
                Connected workspaces sync item metadata in seconds once a source is picked.
              </p>
            </div>

            {/* Items List Card */}
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Items</h2>
                  <p className="text-sm text-slate-500">Pick rows that should appear in the CSV preview.</p>
                </div>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <input
                      type="checkbox"
                      checked={isSelectAllChecked}
                      onChange={(event) => handleSelectAll(event.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Select all
                  </label>
                  <span className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500">
                    {selectedItems.length} / {items.length || 0}&nbsp;
                    <span className="text-slate-400">selected</span>
                  </span>
                </div>
              </div>
              <div className="mt-4 min-h-[240px] overflow-hidden rounded-2xl border border-dashed border-slate-200/80 bg-slate-50/60 p-4">
                {!selectedSource ? (
                  <div className="flex flex-col items-center justify-center gap-3 text-center text-slate-500">
                    <svg
                      className="h-10 w-10 text-slate-400"
                      viewBox="0 0 48 48"
                      fill="none"
                      aria-hidden="true"
                    >
                      <rect x="8" y="10" width="32" height="28" rx="4" stroke="currentColor" strokeWidth="2" />
                      <path
                        d="M16 18h16M16 24h16M16 30h9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    <p className="text-sm font-semibold text-slate-800">No source selected</p>
                    <p className="text-xs text-slate-500">Select a source to load items and begin picking rows.</p>
                  </div>
                ) : isLoadingItems ? (
                  <div className="space-y-3">
                    {Array.from({ length: 3 }).map((_, index) => (
                      <div
                        key={`skeleton-${index}`}
                        className="h-12 animate-pulse rounded-xl bg-slate-200/70"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="max-h-[240px] space-y-3 overflow-y-auto pr-1">
                    {items.map((item) => {
                      const isChecked = selectedItems.includes(item.id);
                      return (
                        <label
                          key={item.id}
                          className="group flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white/80 px-4 py-3 transition hover:border-slate-300"
                        >
                          <span className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleSelectItem(item.id)}
                              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="flex flex-col">
                              <span
                                className="text-sm font-semibold text-slate-900 truncate"
                                title={item.title}
                              >
                                {item.title}
                              </span>
                              <span className="text-xs text-slate-500">
                                {item.date} · {item.category}
                              </span>
                            </span>
                          </span>
                          <span className="text-xs font-semibold text-slate-400 transition group-hover:text-indigo-600">
                            {isChecked ? "Selected" : "Add"}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right column with preview and export */}
          <div className="space-y-6 lg:sticky lg:top-6">
            {/* CSV Preview Card */}
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Preview</h2>
                  <p className="text-xs text-slate-500">See the first few rows that will be exported.</p>
                </div>
                <span className="rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-600">
                  {previewBadgeText}
                </span>
              </div>
              <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
                {previewRows.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 text-center text-slate-500">
                    <svg
                      className="h-12 w-12 text-slate-400"
                      viewBox="0 0 48 48"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M10 16h28v16H10z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16 26h16M26 20h8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    <p className="text-sm font-semibold text-slate-800">No rows selected</p>
                    <p className="text-xs text-slate-500">Select items from the left to start building the CSV.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="grid min-w-full grid-cols-4 gap-4 border-b border-slate-200 pb-2 text-xs uppercase tracking-wide text-slate-500">
                      <span>Title</span>
                      <span>Description</span>
                      <span>Link</span>
                      <span>Image</span>
                    </div>
                    <div className="space-y-3 pt-3">
                      {visiblePreviewRows.map((row) => (
                        <div
                          key={row.id}
                          className="grid min-w-full grid-cols-4 gap-4 rounded-2xl border border-slate-100 bg-white/80 px-3 py-3 text-sm text-slate-700"
                        >
                          <span className="truncate font-semibold" title={row.title}>
                            {row.title}
                          </span>
                          <span className="truncate text-slate-500" title={row.description}>
                            {row.description}
                          </span>
                          <span className="truncate text-indigo-600" title={row.link}>
                            {row.link}
                          </span>
                          <span className="flex items-center justify-center">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
                              {row.imageLabel}
                            </div>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Export Settings Card */}
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Export Settings</h2>
                  <p className="text-sm text-slate-500">Fine-tune the structure before downloading.</p>
                </div>
              </div>
              <div className="mt-4 space-y-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Export name
                  </label>
                  <input
                    type="text"
                    value={exportName}
                    onChange={(event) => setExportName(event.target.value)}
                    placeholder={autoNamePlaceholder}
                    className="mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    This value becomes the filename when the CSV is generated.
                  </p>
                </div>

                {[
                  {
                    key: "removeLinks",
                    label: "Remove links column",
                    helper: "Strip hyperlinks for privacy-sensitive drops."
                  },
                  {
                    key: "includeHeaders",
                    label: "Include headers row",
                    helper: "Keeps column names on row one for clarity."
                  },
                  {
                    key: "optimizeExcel",
                    label: "Optimize for Excel compatibility",
                    helper: "Prevents extra characters that can break Excel imports."
                  }
                ].map((option) => (
                  <div
                    key={option.key}
                    className="flex items-start justify-between gap-4 rounded-2xl border border-slate-100 bg-slate-50/70 px-4 py-3"
                  >
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-slate-900">{option.label}</p>
                      <p className="text-xs text-slate-500">{option.helper}</p>
                    </div>
                    <label className="inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        checked={exportOptions[option.key]}
                        onChange={() => toggleExportOption(option.key)}
                        className="peer sr-only"
                      />
                      <span className="h-5 w-10 rounded-full bg-slate-300 transition peer-checked:bg-indigo-500">
                        <span className="block h-4 w-4 translate-x-1 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions Card */}
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-5 shadow-sm">
              <div className="space-y-4">
                <div className="space-y-2">
                  <h2 className="text-lg font-semibold text-slate-900">Actions</h2>
                  <p className="text-sm text-slate-500">Export the spreadsheet once you are ready.</p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    disabled={isExportDisabled}
                    className={`flex-1 rounded-xl border border-transparent bg-indigo-600 px-4 py-3 text-sm font-semibold text-white transition ${
                      isExportDisabled
                        ? "cursor-not-allowed opacity-50"
                        : "hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    }`}
                  >
                    Generate &amp; Download CSV
                  </button>
                  <button
                    type="button"
                    className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900"
                  >
                    <svg
                      className="h-4 w-4 text-slate-500 transition hover:text-slate-700"
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M5 5h10v10H5z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="m8 11 3-3 3 3"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    View Export History
                  </button>
                </div>
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-500">
                  <p>Select items from the left to enable export.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvGenerator;
